import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const DEFAULT_PREFERENCES = {
  analysisComplete: true,
  promotionalEmails: false,
  securityNotifications: true,
  weeklyDigest: true,
  newFeatures: true,
  creditReminders: true
};

const EmailPreferences = ({ user, onSuccess }) => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [emailFrequency, setEmailFrequency] = useState('daily');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const frequencyOptions = [
    { value: 'realtime', label: 'Real-time (as they happen)' },
    { value: 'daily', label: 'Daily digest' },
    { value: 'weekly', label: 'Weekly digest' },
    { value: 'monthly', label: 'Monthly digest' }
  ];

  // Load saved preferences from Supabase auth user metadata
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.id) { setIsFetching(false); return; }
      try {
        const { data, error } = await supabase?.auth?.getUser();
        const saved = data?.user?.user_metadata?.email_preferences;
        if (!error && saved) {
          setPreferences(prev => ({ ...prev, ...saved?.notifications }));
          if (saved?.frequency) setEmailFrequency(saved?.frequency);
        }
      } catch (err) {
        // Use defaults if metadata fetch fails
      } finally {
        setIsFetching(false);
      }
    };
    loadPreferences();
  }, [user?.id]);

  const handleCheckboxChange = (name) => {
    setPreferences(prev => ({ ...prev, [name]: !prev?.[name] }));
  };

  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      if (user?.id) {
        const currentMetadata = user?.user_metadata || {};
        const { error } = await supabase?.auth?.updateUser({
          data: {
            ...currentMetadata,
            email_preferences: {
              notifications: preferences,
              frequency: emailFrequency
            }
          }
        });

        if (error) {
          throw error;
        }
      }
      onSuccess?.('Email preferences updated successfully');
    } catch (err) {
      onSuccess?.('Unable to update email preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribeAll = () => {
    setPreferences({
      analysisComplete: false,
      promotionalEmails: false,
      securityNotifications: true,
      weeklyDigest: false,
      newFeatures: false,
      creditReminders: false
    });
    onSuccess?.('Unsubscribed from all non-essential emails');
  };

  if (isFetching) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(6)]?.map((_, i) => (
          <div key={i} className="h-14 bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Email Notifications */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} color="#9333EA" />
          Email Notifications
        </h2>
        <p className="text-sm text-gray-600 mb-4 sm:mb-6">
          Choose which emails you'd like to receive from Angle!
        </p>

        <div className="space-y-4 sm:space-y-5">
          <Checkbox
            checked={preferences?.analysisComplete}
            onChange={() => handleCheckboxChange('analysisComplete')}
            label="Analysis Completion Alerts"
            description="Get notified when your photo ranking analysis is complete"
          />

          <Checkbox
            checked={preferences?.creditReminders}
            onChange={() => handleCheckboxChange('creditReminders')}
            label="Credit Balance Reminders"
            description="Receive alerts when your credit balance is running low"
          />

          <Checkbox
            checked={preferences?.weeklyDigest}
            onChange={() => handleCheckboxChange('weeklyDigest')}
            label="Weekly Activity Digest"
            description="Summary of your photo analysis activity and insights"
          />

          <Checkbox
            checked={preferences?.newFeatures}
            onChange={() => handleCheckboxChange('newFeatures')}
            label="New Features & Updates"
            description="Be the first to know about new features and improvements"
          />

          <Checkbox
            checked={preferences?.promotionalEmails}
            onChange={() => handleCheckboxChange('promotionalEmails')}
            label="Promotional Emails"
            description="Special offers, discounts, and credit package deals"
          />

          <Checkbox
            checked={preferences?.securityNotifications}
            onChange={() => handleCheckboxChange('securityNotifications')}
            label="Security Notifications"
            description="Important security alerts and account activity (recommended)"
          />
        </div>
      </div>

      {/* Email Frequency */}
      <div className="border-t pt-6 sm:pt-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <Icon name="Clock" size={20} color="#9333EA" />
          Email Frequency
        </h2>
        <p className="text-sm text-gray-600 mb-4 sm:mb-6">
          Control how often you receive non-urgent notifications
        </p>

        <Select
          label="Notification Frequency"
          options={frequencyOptions}
          value={emailFrequency}
          onChange={setEmailFrequency}
          description="This setting applies to analysis alerts and activity digests"
        />
      </div>

      {/* Actions */}
      <div className="border-t pt-6 sm:pt-8 flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleSavePreferences}
          loading={isLoading}
          iconName="Save"
          className="w-full sm:w-auto min-h-[44px]"
        >
          Save Preferences
        </Button>
        <Button
          variant="outline"
          onClick={handleUnsubscribeAll}
          iconName="MailX"
          className="w-full sm:w-auto min-h-[44px]"
        >
          Unsubscribe from All
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Icon name="Info" size={20} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">About Email Preferences</p>
          <p className="text-sm text-blue-700 mt-1">
            You can update these preferences at any time. Security notifications are always enabled to keep your account safe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailPreferences;